<template>
  <div class="infoboard-description">
		<h4 class="infoboard-description__title g-text g-text--l">
			<span>{{ computedSaving }}</span>
			<span
				v-if="computedAnimatedFlip"
				class="g-flip-card g-text g-text--xl g-margin--0"
				:class="{'g-flip-card--flip': rotation.isProduction ? !rotation.description.savings : rotation.description.savings}"
			>
				<FontAwesomeIcon :icon="['fal', 'exclamation-circle']" />
				<FontAwesomeIcon :icon="['fal', 'check-circle']" />
			</span>
			<!-- Fallback for missing CSS backface-visibility -->
			<span v-else>
				<FontAwesomeIcon
					v-if="rotation.isProduction ? !rotation.description.savings : rotation.description.savings"
					:icon="['fal', 'check-circle']"
				/>
				<FontAwesomeIcon
					v-else
					:icon="['fal', 'exclamation-circle']"
				/>
			</span>
		</h4>
		<div class="g-container g-container--no-top-border g-container--pass-padding">
			<div
				class="infoboard-description__number g-text g-text--center g-text--3xl g-text--center"
			>
				<CountUp
					class="g-width--full"
					:key="computedUnit"
					:counter="Math.abs(rotation.description.changes.value)"
					:formatter="computedCountUpFormatter"
				>
					<span class="g-text--xl">{{ computedUnit }}</span>
				</CountUp>
			</div>
			<small class="g-text g-text--m">
				{{ $t(`sharedTexts.comparisons.${rotation.comparison}.${rotation.resolution}.comparisonDescription`) }} <br/>
				<small class="no-reference-text">{{ computedHasNoReferenceData ? $t('sharedTexts.NoReferenceData') : '' }}</small>
			</small>
		</div>
		<div
			v-if="computedEqualTo"
			ref="text"
			class="g-container g-container--no-top-border g-container--pass-padding"
		>
			<h3 class="g-text g-text--title g-text--m">
				{{ $t('sharedTexts.funFact') }}
			</h3>
			<p class="g-text g-text--m">
				{{ computedEqualTo }}
			</p>
		</div>
  </div>
</template>

<script lang="ts">
import CountUp from '@/components/CountUp.vue'
import { store } from '@libs/store'
import { defineComponent } from 'vue'
import { Rotation } from '@libs/interfaces/interfaces'
import { getEqualTo } from '@libs/models/rotations'
import { currencySymbol, normalizeArrayWithCurrency, numberFormatterManualDigits } from '@enity/formatting'
import { formatCurrencyUnitToHighest } from '@/assets/libs/models/formatters'

export default defineComponent({
	name: 'InfoboardDescription',
	props: {
		rotation: {
			type: Object as () => Rotation,
			required: true
		},
		locale: {
			type: String,
			default: 'en-GB'
		}
	},
	components: {
		CountUp
	},
	computed: {
		computedHasNoReferenceData(): boolean {
			return !this.rotation.chart.comparisonPartialData?.some(value => value) && this.rotation.comparison === 'periodically'
		},
		computedUnit(): string {
			if (this.rotation.description.changes.type === 'percentage') return '%'

			return this.rotation.description.changes.value >= 1E6
				? formatCurrencyUnitToHighest(
					this.computedLocale,
					this.rotation.unit,
					[this.rotation.description.changes.value]
				).scaledSymbol
				: currencySymbol(this.computedLocale, this.rotation.unit, { display: 'symbol' })
		},
		computedCountUpFormatter(): ((value: number) => string) | null {
			if (this.rotation.description.changes.type === 'percentage') {
				return numberFormatterManualDigits(
					this.computedLocale,
					{ maximumFractionDigits: 0 }
				)
			}

			const decimals = this.rotation.description.changes.value >= 1E6 ? 1 : 0

			return (val: number): string => {
				if (decimals) {
					const normalized = normalizeArrayWithCurrency(
						this.computedLocale,
						this.rotation.unit,
						[this.rotation.description.changes.value, val]
					).values[1]
					if (normalized !== undefined) {
						val = normalized
					}
				}

				return numberFormatterManualDigits(
					this.computedLocale, {
						minimumFractionDigits: decimals,
						maximumFractionDigits: decimals
					}
				)(val)
			}
		},
		computedAnimatedFlip() {
			const niceSupport = store.getData('featureSupport.nice') as Record<string, unknown>[]
			const backfaceVisibility = niceSupport.find(feature => feature.name === 'css-backface-visibility')

			return backfaceVisibility ? backfaceVisibility.support : false
		},
		computedEqualTo(): string {
			return this.rotation.description.equalTo.type
				? getEqualTo(this.rotation)
				: ''
		},
		computedSaving(): string {
			const state = this.rotation.description.savings

			return state
				? this.$t('sharedTexts.saving')
				: this.$t('sharedTexts.expense')
		},
		computedLocale(): string {
			return this.locale
		}
	},
	watch: {
		rotation() {
			this.$nextTick(() => { // should probably be a vue transition
				const text = this.$refs.text as HTMLElement | undefined
				if (text) {
					text.classList.remove('animation--fade')

					setTimeout(() => {
						text.classList.add('animation--fade')
					}, 0)
				}
			})
		}
	}
})
</script>

<style lang="scss">
.infoboard-description {
	position: relative;

	.no-reference-text {
		font-size: 80%;
	}

	.g-container {
		margin: 0;
	}

	&__title {
		display: flex;
		align-items: center;
		justify-content: center;

		> *:not(:first-child) {
			margin-left: 8px;
		}
	}

	&__number {
		font-family: $font-secondary;
		font-weight: 100;
		// position: relative;
		// height: 70px;
		margin: 30px 0 15px;

		> * {
			// position: absolute;
			// top: 50%;
			// left: 50%;
			// transform: translate3d(-50%, -50%, 0);
			white-space: nowrap;
		}
	}
}
</style>
